"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ChevronDown, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
interface MenuItemType {
  id: string | number;
  label: string;
  href: string;
  children?: MenuItemType[];
}
const fallbackItems: MenuItemType[] = [{
  id: "home",
  label: "Ana Sayfa",
  href: "/"
}, {
  id: "areas",
  label: "Faaliyet Alanları",
  href: "/AreasOfExpertisePage"
}, {
  id: "hakkimizda",
  label: "Hakkımızda",
  href: "#",
  children: [{
    id: "hakkimizda-firm",
    label: "Yüksekbaş Hukuk Bürosu",
    href: "/SupremeLawOfficePage"
  }, {
    id: "hakkimizda-founder",
    label: "Kurucumuz",
    href: "/FounderAttorneyPage"
  }]
}, {
  id: "duyurular",
  label: "Duyurular",
  href: "/AnnouncementsPage"
}, {
  id: "kvkk",
  label: "Kişisel Verilerin Korunması",
  href: "#",
  children: [{
    id: "kvkk-notice",
    label: "Aydınlatma Metni",
    href: "/ContactFormPrivacyNoticePage"
  }, {
    id: "kvkk-policy",
    label: "Politika Metni",
    href: "/PersonalDataProtectionAndProcessingPolicyPage"
  }]
}, {
  id: "contact",
  label: "İletişim",
  href: "/ContactPage"
}];
const allowedLabels = new Set(["Ana Sayfa", "Faaliyet Alanları", "Hakkımızda", "Kişisel Verilerin Korunması", "Duyurular", "İletişim"]);
function formatMenuLabel(label: string) {
  const normalized = (label || "").trim().toLocaleLowerCase("tr");
  if (["ana sayfa", "anasayfa", "home", "homepage"].includes(normalized)) {
    return "Ana Sayfa";
  }
  if (["uzmanlık alanları", "uzmanlik alanlari", "faaliyet alanları", "faaliyet alanlari"].includes(normalized)) {
    return "Faaliyet Alanları";
  }
  if (["hakkımızda", "hakkimizda", "about", "about us"].includes(normalized)) {
    return "Hakkımızda";
  }
  if (["kişisel verilerin korunması", "kisisel verilerin korunmasi", "kvkk"].includes(normalized)) {
    return "Kişisel Verilerin Korunması";
  }
  if (["duyurular", "announcements", "duyuru"].includes(normalized)) {
    return "Duyurular";
  }
  if (["aydınlatma metni", "aydinlatma metni", "iletişim formu aydınlatma metni"].includes(normalized)) {
    return "Aydınlatma Metni";
  }
  if (["politika metni", "kişisel verilerin korunması ve işlenmesi politikası"].includes(normalized)) {
    return "Politika Metni";
  }
  if (["iletişim", "iletisim", "contact"].includes(normalized)) {
    return "İletişim";
  }
  return label;
}
function getMenuItems(menuItems: any[] | undefined, loading: boolean): MenuItemType[] {
  let baseItems: MenuItemType[];
  if (loading || !menuItems || menuItems.length === 0) {
    baseItems = fallbackItems;
  } else {
    const formatItem = (item: any): MenuItemType => {
      const formattedChildren = Array.isArray(item.children) && item.children.length > 0 ? item.children.map((child: any) => formatItem(child)) : undefined;
      return {
        id: item.id,
        label: formatMenuLabel(item.label || item.title || ""),
        href: item.href || item.url || "#",
        ...(formattedChildren ? {
          children: formattedChildren
        } : {})
      };
    };
    const formatted = menuItems.map(formatItem);
    const seen = new Set<string>();
    const filtered = formatted.filter(item => {
      if (!allowedLabels.has(item.label) || seen.has(item.label)) {
        return false;
      }
      seen.add(item.label);
      return true;
    });
    baseItems = filtered.length > 0 ? filtered : fallbackItems;
  }
  const finalItems: MenuItemType[] = [...baseItems];
  if (!finalItems.some(item => item.label === "Hakkımızda")) {
    const kvkkIndex = finalItems.findIndex(item => item.label === "Kişisel Verilerin Korunması");
    const hakkimizdaItem: MenuItemType = {
      id: "hakkimizda",
      label: "Hakkımızda",
      href: "#",
      children: [{
        id: "hakkimizda-firm",
        label: "Yüksekbaş Hukuk Bürosu",
        href: "/SupremeLawOfficePage"
      }, {
        id: "hakkimizda-founder",
        label: "Kurucumuz",
        href: "/FounderAttorneyPage"
      }]
    };
    if (kvkkIndex !== -1) {
      finalItems.splice(kvkkIndex, 0, hakkimizdaItem);
    } else {
      finalItems.push(hakkimizdaItem);
    }
  }
  if (!finalItems.some(item => item.label === "Duyurular")) {
    const kvkkIndex = finalItems.findIndex(item => item.label === "Kişisel Verilerin Korunması");
    const contactIndex = finalItems.findIndex(item => item.label === "İletişim");
    const duyurularItem: MenuItemType = {
      id: "duyurular",
      label: "Duyurular",
      href: "/AnnouncementsPage"
    };
    if (kvkkIndex !== -1) {
      finalItems.splice(kvkkIndex + 1, 0, duyurularItem);
    } else if (contactIndex !== -1) {
      finalItems.splice(contactIndex, 0, duyurularItem);
    } else {
      finalItems.push(duyurularItem);
    }
  }
  const canonicalOrder = ["Ana Sayfa", "Faaliyet Alanları", "Hakkımızda", "Duyurular", "Kişisel Verilerin Korunması", "İletişim"];
  finalItems.sort((a, b) => {
    const aIndex = canonicalOrder.indexOf(a.label);
    const bIndex = canonicalOrder.indexOf(b.label);
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return 0;
  });
  return finalItems;
}
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const items = getMenuItems(menuItems, loading);
  const textItems = items.filter(item => item.label !== "İletişim");
  const contactItem = items.find(item => item.label === "İletişim") || fallbackItems[fallbackItems.length - 1];
  return <nav aria-label="Ana navigasyon" className="hidden lg:block">
      <ul className="flex items-center gap-8 text-sm text-[#f4efe5]/76">
        {textItems.map(item => {
        if (item.children && item.children.length > 0) {
          return <li key={item.id} className="group relative">
                <Button type="button" variant="ghost" className="h-auto p-0 inline-flex min-h-11 items-center gap-1.5 rounded-none border-b border-transparent bg-transparent font-sans text-sm font-normal text-[#f4efe5]/76 shadow-none hover:border-[#f4efe5]/40 hover:bg-transparent hover:text-[#f4efe5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4efe5] focus-visible:ring-offset-2 focus-visible:ring-offset-[#090909]" aria-haspopup="true">
                  <span>{item.label}</span>
                  <ChevronDown className="size-3.5 opacity-70 transition-transform duration-200 group-hover:rotate-180 group-focus-within:rotate-180" />
                </Button>
                <div className="invisible absolute left-0 top-full z-50 min-w-[220px] pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                  <div className="border border-[#f4efe5]/16 bg-[#090909] p-1.5 shadow-xl">
                    {item.children.map(child => <Link key={child.id} to={child.href} className="block px-3.5 py-2 font-sans text-sm text-[#f4efe5]/80 transition-colors hover:bg-[#f4efe5]/10 hover:text-[#f4efe5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4efe5] focus-visible:ring-offset-2 focus-visible:ring-offset-[#090909]">
                        {child.label}
                      </Link>)}
                  </div>
                </div>
              </li>;
        }
        return <li key={item.id}>
              <Link to={item.href} className="inline-flex min-h-11 items-center border-b border-transparent font-sans transition-colors hover:border-[#f4efe5]/40 hover:text-[#f4efe5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4efe5] focus-visible:ring-offset-2 focus-visible:ring-offset-[#090909]">
                {item.label}
              </Link>
            </li>;
      })}
        <li key={contactItem.id}>
          <Link to={contactItem.href} className="inline-flex min-h-11 items-center border border-[#f4efe5]/28 px-5 font-sans text-sm text-[#f4efe5] transition-colors hover:border-[#f4efe5]/70 hover:bg-[#f4efe5]/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4efe5] focus-visible:ring-offset-2 focus-visible:ring-offset-[#090909]">
            {contactItem.label}
          </Link>
        </li>
      </ul>
    </nav>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const items = getMenuItems(menuItems, loading);
  return <nav aria-label="Mobil navigasyon" className="px-6 pb-8 pt-4">
      <ul className="divide-y divide-[#f4efe5]/12 border-y border-[#f4efe5]/12">
        {items.map(item => {
        if (item.children && item.children.length > 0) {
          return <li key={item.id} className="py-3">
                <div className="flex min-h-[44px] items-center justify-between font-serif text-2xl text-[#f4efe5]">
                  <span>{item.label}</span>
                  <span aria-hidden="true" className="font-sans text-sm text-[#f4efe5]/45">/</span>
                </div>
                <ul className="mt-2 space-y-1 border-l border-[#f4efe5]/16 pl-4">
                  {item.children.map(child => <li key={child.id}>
                      <Link to={child.href} className="flex min-h-[44px] items-center py-1.5 font-sans text-base text-[#f4efe5]/80 transition-colors hover:text-[#f4efe5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4efe5] focus-visible:ring-offset-2 focus-visible:ring-offset-[#090909]">
                        {child.label}
                      </Link>
                    </li>)}
                </ul>
              </li>;
        }
        return <li key={item.id}>
              <Link to={item.href} className="flex min-h-[56px] items-center justify-between py-3 font-serif text-2xl text-[#f4efe5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4efe5] focus-visible:ring-offset-2 focus-visible:ring-offset-[#090909]">
                <span>{item.label}</span>
                <span aria-hidden="true" className="font-sans text-sm text-[#f4efe5]/45">/</span>
              </Link>
            </li>;
      })}
      </ul>
    </nav>;
}
function Wordmark() {
  return <Link to="/" aria-label="Yüksekbaş Hukuk ve Danışmanlık ana sayfa" className="group flex min-h-12 min-w-0 items-center text-[#f4efe5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4efe5] focus-visible:ring-offset-2 focus-visible:ring-offset-[#090909]">
      <span className="max-w-[118px] min-w-0 font-serif text-[19px] leading-[0.96] sm:max-w-none sm:text-[22px] lg:text-[24px]">
        <span className="block truncate sm:inline sm:truncate-none">Yüksekbaş</span>
        <span className="block truncate sm:inline sm:truncate-none"> Hukuk ve Danışmanlık</span>
      </span>
    </Link>;
}
function HeaderInner() {
  return <header className="sticky top-0 z-50 border-b border-[#f4efe5]/12 bg-[#090909]/94 backdrop-blur-md">
      <div className="mx-auto flex h-20 w-full max-w-[1320px] items-center justify-between gap-4 px-5 sm:px-8 lg:px-10">
        <Wordmark />
        <div className="flex min-w-0 items-center gap-2">
          <DesktopNav />
          <div className="lg:hidden">
            <SafeHydrate><Drawer direction="right">
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="size-11 min-h-[44px] min-w-[44px] rounded-none border border-[#f4efe5]/14 text-[#f4efe5] hover:bg-[#f4efe5]/10" aria-label="Menü aç">
                  <Menu className="size-5" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="h-full w-[88vw] border-l border-[#f4efe5]/16 bg-[#090909] text-[#f4efe5] sm:max-w-sm">
                <DrawerHeader className="border-b border-[#f4efe5]/12 px-6 text-left">
                  <DrawerTitle className="font-serif text-2xl font-normal leading-none text-[#f4efe5]">
                    Yüksekbaş Hukuk ve Danışmanlık
                  </DrawerTitle>
                </DrawerHeader>
                <MobileNav />
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>
        </div>
      </div>
    </header>;
}
export default function Header() {
  return <MenuProvider menu_id="40">
      <HeaderInner />
    </MenuProvider>;
}